import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { useMediaQuery } from 'react-responsive';

import Carousel from 'components/Carousel';
import { Container } from 'components/Grid';
import { SecondaryHeading } from 'components/Headings';
import Image from 'components/ImageV2';
import Tabs from 'components/Tabs';
import { IMAGE_RATIOS, IMAGE_WIDTHS } from 'constants/images';
import { largeUp, mediumUp } from 'constants/mediaQueries';
import { TAB_LABELS } from 'containers/NotFoundPageExploreMoreContainer/constants';
import Theme from 'styles/theme';
import I18n from 'utils/I18n';

const NotFoundPageExploreMore = ({ cards, trackClick }) => {
  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
  const isMediumUp = useMediaQuery({ query: mediumUp });
  const handleTabClick = tabIndex => {
    setActiveTabIndex(tabIndex);
    setCurrentSlideIndex(0);
  };
  const currentCardsList = cards[activeTabIndex].list;
  const tabLabels = TAB_LABELS.map(label => I18n.localize(label).toUpperCase());
  const trackCTAClick = (name, url) => () =>
    trackClick({
      name,
      url,
    });

  return (
    <div className="NotFoundPageExploreMore">
      <Container>
        <SecondaryHeading className="NotFoundPageExploreMore__heading">
          {I18n.localize('not-found.explore-more')}
        </SecondaryHeading>

        <div className="NotFoundPageExploreMore__tabs-container">
          <Tabs
            tabs={tabLabels}
            activeTabIndex={activeTabIndex}
            onTabClick={handleTabClick}
            isButton
            fullWidthUnderLine
          />
        </div>

        {isMediumUp ? (
          <ul className="NotFoundPageExploreMore__cards-list">
            {currentCardsList.map((card, index) => {
              const localizedLabel = I18n.localize(card.label);
              return (
                <li key={index} className="NotFoundPageExploreMore__cards-list-item">
                  <a
                    className="NotFoundPageExploreMore__list-item-link"
                    href={card.url}
                    onClick={trackCTAClick(localizedLabel, card.url)}
                  >
                    <Image
                      alt={localizedLabel}
                      src={card.imageUrl}
                      width={IMAGE_WIDTHS.W_1000}
                      ratio={IMAGE_RATIOS.R_1x1}
                    />
                    <span className="NotFoundPageExploreMore__cards-list-item-label">
                      {localizedLabel}
                    </span>
                  </a>
                </li>
              );
            })}
          </ul>
        ) : (
          <Carousel
            options={{ perPage: 2.05, threshold: 4, draggable: true, loop: false }}
            currentSlide={currentSlideIndex}
            onChange={setCurrentSlideIndex}
          >
            {currentCardsList.map((card, index) => {
              const localizedLabel = I18n.localize(card.label);
              return (
                <div key={index} className="NotFoundPageExploreMore__cards-list-item">
                  <a
                    className="NotFoundPageExploreMore__list-item-link"
                    href={card.url}
                    onClick={trackCTAClick(localizedLabel, card.url)}
                  >
                    <Image
                      alt={localizedLabel}
                      src={card.imageUrl}
                      width={IMAGE_WIDTHS.W_1000}
                      ratio={IMAGE_RATIOS.R_1x1}
                    />
                    <span className="NotFoundPageExploreMore__cards-list-item-label">
                      {localizedLabel}
                    </span>
                  </a>
                </div>
              );
            })}
          </Carousel>
        )}
      </Container>

      <style jsx>{`
        .NotFoundPageExploreMore {
          position: relative;
          padding-bottom: ${Theme.get('spacing') * 8}px;
          background-color: ${Theme.get('colors-black-10')};
          overflow: hidden;
        }

        .NotFoundPageExploreMore__list-item-link {
          display: block;
          height: 100%;
          text-decoration: none;
          background-color: ${Theme.get('colors-white')};
        }

        .NotFoundPageExploreMore__tabs-container {
          margin-bottom: ${Theme.get('spacing') * 3}px;
        }

        .NotFoundPageExploreMore__cards-list-item-label {
          display: block;
          padding: ${Theme.get('spacing') * 2}px;
          font-weight: ${Theme.get('font-weight-semi-bold')};
          font-size: ${Theme.get('font-size-description-px')};
          min-height: 45px;
          display: flex;
          align-items: center;
        }

        .NotFoundPageExploreMore__cards-list-item {
          height: 100%;
        }

        @media ${mediumUp} {
          .NotFoundPageExploreMore {
            padding-bottom: ${Theme.get('spacing') * 12}px;
          }

          .NotFoundPageExploreMore__cards-list {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
            grid-column-gap: ${Theme.get('spacing') * 4}px;
            margin: 0;
            padding: 0 ${Theme.get('spacing') * 2}px;
            list-style: none;
          }

          .NotFoundPageExploreMore__cards-list-item {
            flex-grow: 1;
          }

          .NotFoundPageExploreMore__tabs-container {
            margin: auto;
            margin-bottom: ${Theme.get('spacing') * 4}px;
            max-width: 406px;
          }

          .NotFoundPageExploreMore__cards-list-item-label {
            min-height: 55px;
          }
        }

        @media ${largeUp} {
          .NotFoundPageExploreMore__cards-list {
            padding: 0 ${Theme.get('spacing') * 8}px;
          }
        }
      `}</style>
      <style jsx global>{`
        .page-wrapper.not-found .HomepageV2Carousel {
          padding-bottom: ${Theme.get('spacing') * 8}px;
        }

        .page-wrapper.not-found .DYCarouselRecs {
          padding-top: 0;
        }

        .NotFoundPageExploreMore__heading.typography--secondary-heading {
          text-align: center;
          margin-bottom: ${Theme.get('spacing') * 2}px;
        }

        .NotFoundPageExploreMore .Carousel {
          overflow: visible !important;
        }

        .NotFoundPageExploreMore .Carousel > div {
          display: flex;
          column-gap: ${Theme.get('spacing-px')};
        }

        .NotFoundPageExploreMore .Tabs__active-tab-indicator {
          transition: left ${Theme.get('transition-duration-ms')}
            ${Theme.get('transition-timing-ease-in-out')};
        }

        @media ${mediumUp} {
          .page-wrapper.not-found .HomepageV2Carousel {
            padding-bottom: ${Theme.get('spacing') * 12}px;
          }
        }
      `}</style>
    </div>
  );
};

NotFoundPageExploreMore.propTypes = {
  cards: PropTypes.arrayOf(
    PropTypes.shape({
      list: PropTypes.arrayOf(
        PropTypes.shape({
          label: PropTypes.string.isRequired,
          imageUrl: PropTypes.string.isRequired,
          url: PropTypes.string.isRequired,
        })
      ).isRequired,
    })
  ).isRequired,
  trackClick: PropTypes.func.isRequired,
};

export default NotFoundPageExploreMore;
